var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { Fragment } from 'react';
import { createUseStyles } from 'react-jss';
import { useTheme } from '../../theme';
import { useMultipleMemoClasses, useMemoClasses } from '../../utils/hooks';
var useStyles = createUseStyles({
    primary: {
        '--RUI-slider-main': 'var(--RUI-color-primary)',
        '--RUI-slider-dark': 'var(--RUI-color-primary-dark)',
        '--RUI-slider-light': 'var(--RUI-color-primary-lighter)',
    },
    secondary: {
        '--RUI-slider-main': 'var(--RUI-color-secondary)',
        '--RUI-slider-dark': 'var(--RUI-color-secondary-dark)',
        '--RUI-slider-light': 'var(--RUI-color-secondary-lighter)',
    },
    neutral: {
        '--RUI-slider-main': 'var(--RUI-color-neutral)',
        '--RUI-slider-dark': 'var(--RUI-color-neutral-dark)',
        '--RUI-slider-light': 'var(--RUI-color-neutral-lighter)',
    },
    error: {
        '--RUI-slider-main': 'var(--RUI-color-error)',
        '--RUI-slider-dark': 'var(--RUI-color-error-dark)',
        '--RUI-slider-light': 'var(--RUI-color-error-lighter)',
    },
    warning: {
        '--RUI-slider-main': 'var(--RUI-color-warning)',
        '--RUI-slider-dark': 'var(--RUI-color-warning-dark)',
        '--RUI-slider-light': 'var(--RUI-color-warning-lighter)',
    },
    success: {
        '--RUI-slider-main': 'var(--RUI-color-success)',
        '--RUI-slider-dark': 'var(--RUI-color-success-dark)',
        '--RUI-slider-light': 'var(--RUI-color-success-lighter)',
    },
    active: {},
    disabled: {},
    base: {
        height: '24px',
        margin: 'var(--RUI-margin)',
        position: 'relative',
        boxSizing: 'border-box',
    },
    baseOverrides: function (theme) { return (__assign({}, theme.overrides.Slider.base)); },
    track: {
        width: '100%',
        height: '6px',
        borderRadius: 'var(--RUI-border-radius)',
        backgroundColor: 'var(--RUI-color-paper-darker)',
        display: 'block',
        overflow: 'hidden',
        position: 'relative',
        cursor: 'pointer',
        'input:disabled ~ &': {
            cursor: 'default',
        },
    },
    trackOverrides: function (theme) { return (__assign({}, theme.overrides.Slider.track)); },
    fill: {
        height: '100%',
        display: 'block',
        backgroundColor: 'var(--RUI-slider-main)',
        '$base$disabled &': {
            backgroundColor: 'var(--RUI-color-neutral-dark)',
        },
    },
    fillOverrides: function (theme) { return (__assign({}, theme.overrides.Slider.fill)); },
    handle: {
        padding: '0',
        width: '6px',
        height: '6px',
        borderRadius: '16px',
        border: '4px solid var(--RUI-slider-dark)',
        backgroundColor: 'var(--RUI-color-paper)',
        outline: 'none',
        display: 'block',
        position: 'absolute',
        top: '-4px',
        transform: 'translateX(-50%)',
        cursor: 'grab',
        boxSizing: 'content-box',
        '&:disabled': {
            cursor: 'auto',
            borderColor: 'var(--RUI-color-neutral-dark)',
        },
        '&::before': {
            content: '""',
            display: 'block',
            width: '6px',
            height: '6px',
            backgroundColor: 'transparent',
            border: '0px solid var(--RUI-slider-main)',
            borderRadius: '100px',
            opacity: 0.5,
            transition: 'border-width 0.2s',
            transform: 'translate(-50%, -50%)',
            left: '50%',
            top: '50%',
            position: 'absolute',
        },
        '&:hover:not(:disabled)': {
            '&::before': {
                borderWidth: '8px',
            },
        },
    },
    handleOverrides: function (theme) { return (__assign({}, theme.overrides.Slider.handle)); },
    handleActive: {
        '&::before': {
            cursor: 'grabbing',
            borderWidth: '12px !important',
        },
    },
    mark: {
        display: 'block',
        position: 'absolute',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        height: '100%',
        width: '2px',
        backgroundColor: 'var(--RUI-color-paper-dark)',
        '&$active': {
            backgroundColor: 'var(--RUI-slider-light)',
        },
        '$base$disabled &$active': {
            backgroundColor: 'var(--RUI-slider-light)',
        },
    },
    markOverrides: function (theme) { return (__assign({}, theme.overrides.Slider.mark)); },
    label: {
        position: 'absolute',
        cursor: 'pointer',
        transform: 'translate(-50%)',
        marginTop: 'var(--RUI-margin)',
        font: 'var(--RUI-font-caption)',
    },
    labelOverrides: function (theme) { return (__assign({}, theme.overrides.Slider.label)); },
    withLabels: {
        height: '32px',
    },
    withLabelsOverrides: function (theme) { return (__assign({}, theme.overrides.Slider.label)); },
}, { name: 'RUISlider' });
var SliderView = function (_a) {
    var moving = _a.moving, marks = _a.marks, fill = _a.fill, trackRef = _a.trackRef, handleHandleMouseDown = _a.handleHandleMouseDown, handleLabelClick = _a.handleLabelClick, handleTrackClick = _a.handleTrackClick, value = _a.value, className = _a.className, color = _a.color, style = _a.style, disabled = _a.disabled, withLabels = _a.withLabels, props = __rest(_a, ["moving", "marks", "fill", "trackRef", "handleHandleMouseDown", "handleLabelClick", "handleTrackClick", "value", "className", "color", "style", "disabled", "withLabels"]);
    var theme = useTheme();
    var styles = useStyles(theme);
    var _b = useMultipleMemoClasses([
        [styles.fill, theme.overrides.Slider.fill && styles.fillOverrides, 'RUISlider-fill'],
        [styles.track, theme.overrides.Slider.track && styles.trackOverrides, 'RUISlider-track'],
        [styles.mark, theme.overrides.Slider.mark && styles.markOverrides, 'RUISlider-mark'],
        [styles.label, theme.overrides.Slider.label && styles.labelOverrides, 'RUISlider-label'],
    ], [theme]), fillClasses = _b[0], trackClasses = _b[1], markClasses = _b[2], labelClasses = _b[3];
    var baseClasses = useMemoClasses([
        styles.base,
        styles[color],
        theme.overrides.Slider.base && styles.baseOverrides,
        disabled && styles.disabled,
        withLabels && styles.withLabels,
        withLabels && theme.overrides.Slider.withLabels && styles.withLabelsOverrides,
        'RUISlider-base',
        className,
    ], [theme, withLabels, disabled, className, color]);
    var handleClasses = useMemoClasses([
        styles.handle,
        theme.overrides.Slider.handle && styles.handleOverrides,
        moving && styles.handleActive,
        'RUISlider-handle',
    ], [theme, moving]);
    return (React.createElement("div", { className: baseClasses, style: style },
        React.createElement("input", __assign({ type: "hidden", onChange: function () { }, value: value, disabled: disabled }, props)),
        React.createElement("span", { className: trackClasses, ref: trackRef, onClick: handleTrackClick },
            React.createElement("span", { className: fillClasses, style: { width: fill } }),
            marks.map(function (mark, index) { return (React.createElement(Fragment, { key: index },
                React.createElement("span", { className: "".concat(markClasses, " ").concat(mark.active && styles.active), style: { left: mark.position } }))); })),
        marks.map(function (mark, index) { return (React.createElement(Fragment, { key: index }, mark.label && (React.createElement("span", { className: labelClasses, style: { left: mark.position }, onClick: function (e) { return handleLabelClick(e, index); } }, mark.label)))); }),
        React.createElement("button", { role: "handle", className: handleClasses, style: { left: fill }, onMouseDown: handleHandleMouseDown, disabled: disabled })));
};
export default SliderView;
